// Chart spec -> ECharts option compiler. Options are built field by field from the validated
// spec; agent JSON is never handed to ECharts, whose options accept functions and HTML in
// places (formatter, tooltip). Value formatting is attached afterwards by trusted renderer
// code using the declarative `format` hint returned here.
import * as s from './schema-dsl.ts'
import { INTENTS, SEVERITIES, chartTheme, type ChartTheme, type Intent, type Severity } from './tokens.ts'
import { VALUE_FORMATS, formatValue, type ValueFormat } from './format.ts'

export type EChartsModule =
  | 'BarChart' | 'LineChart' | 'PieChart' | 'ScatterChart' | 'SankeyChart' | 'TreemapChart'
  | 'SunburstChart' | 'HeatmapChart' | 'GaugeChart' | 'FunnelChart' | 'RadarChart'
  | 'CandlestickChart' | 'GraphChart' | 'BoxplotChart'
  | 'GridComponent' | 'TooltipComponent' | 'LegendComponent' | 'MarkLineComponent' | 'TitleComponent'
  | 'AriaComponent' | 'VisualMapComponent' | 'CalendarComponent' | 'DataZoomComponent' | 'RadarComponent'

export interface TableFallback {
  columns: { key: string, label: string, format?: ValueFormat, align?: 'start' | 'end' }[]
  rows: Record<string, string | number | null>[]
}

export interface CompiledChart {
  option: Record<string, unknown>
  modules: EChartsModule[]
  renderer: 'svg' | 'canvas'
  table: TableFallback
  summary: string
  valueFormat: { format: ValueFormat, currency?: string }
}

export interface ChartLabels {
  category: string
  value: string
  share: string
  series: string
  severity: Record<Severity, string>
  from: string
  to: string
  date: string
  open: string
  close: string
  low: string
  high: string
  conversion: string
  [key: string]: unknown
}

export const DEFAULT_CHART_LABELS: ChartLabels = {
  category: 'Category',
  value: 'Value',
  share: 'Share',
  series: 'Series',
  severity: { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low', info: 'Info' },
  from: 'From',
  to: 'To',
  date: 'Date',
  open: 'Open',
  close: 'Close',
  low: 'Low',
  high: 'High',
  conversion: 'Conversion'
}

export interface CompileOptions {
  preset?: string
  locale?: string
  labels?: Partial<ChartLabels>
  reducedMotion?: boolean
}

export interface ChartContext {
  theme: ChartTheme
  locale: string
  labels: ChartLabels
  animation: Record<string, unknown>
}

export interface ChartKindDef {
  schema: s.Schema
  compile (spec: any, ctx: ChartContext): CompiledChart
}

// ---------------------------------------------------------------------------
// Shared schema pieces

export const LABEL = s.string({ max: 80 })
const intent = s.enumOf(INTENTS)
const valueFormat = s.enumOf(VALUE_FORMATS)
const currency = s.string({ min: 3, max: 3, pattern: /^[A-Z]{3}$/ })
const nullableNumber = s.anyOf([s.number(), s.nullValue()])

export const commonChartProps = {
  format: valueFormat,
  currency,
  showLegend: s.boolean(),
  title: s.string({ max: 120 })
}

const referenceLine = s.object({
  value: s.number(),
  label: s.string({ max: 60 }),
  intent
}, { required: ['value'] })

export const seriesStyleProps = {
  intent,
  render: s.enumOf(['bar', 'line', 'area'], 'Mix bars and lines in one chart'),
  stack: s.string({ max: 20, pattern: /^[A-Za-z0-9_-]+$/ }),
  axis: s.enumOf(['left', 'right'])
}

const cartesianSeries = s.object({
  name: LABEL,
  data: s.array(nullableNumber, { max: 1000 }),
  ...seriesStyleProps
}, { required: ['name', 'data'] })

export const CARTESIAN_KINDS = ['bar', 'line', 'area'] as const

export const cartesianOptionProps = {
  horizontal: s.boolean(),
  stacked: s.boolean(),
  smooth: s.boolean(),
  xLabel: s.string({ max: 60 }),
  yLabel: s.string({ max: 60 }),
  rightLabel: s.string({ max: 60 }),
  referenceLines: s.array(referenceLine, { max: 5 }),
  zoom: s.boolean('Adds a range slider for long series'),
  ...commonChartProps
}

function cartesianSchema (kind: string) {
  return s.object({
    kind: s.literal(kind),
    categories: s.array(s.string({ max: 80 }), { min: 1, max: 1000 }),
    series: s.array(cartesianSeries, { min: 1, max: 12 }),
    ...cartesianOptionProps
  }, {
    required: ['kind', 'categories', 'series'],
    refine (v, _path, report) {
      const n = (v.categories as unknown[]).length
      ;(v.series as { data: unknown[] }[]).forEach((series, i) => {
        if (series.data.length !== n) report(`series[${i}].data`, `must have one value per category (${n}), got ${series.data.length}`)
      })
    }
  })
}

// ---------------------------------------------------------------------------
// Helpers

export function withAlpha (hex: string, alpha: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex)
  if (!m) return hex
  const n = parseInt(m[1]!, 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
}

export function colorFor (ctx: ChartContext, i: number, intentName?: Intent): string {
  return intentName && intentName !== 'neutral' ? ctx.theme.intents[intentName] : ctx.theme.palette[i % ctx.theme.palette.length]!
}

export function baseOption (ctx: ChartContext): Record<string, unknown> {
  return {
    aria: { enabled: true, decal: { show: false } },
    backgroundColor: 'transparent',
    textStyle: { fontFamily: ctx.theme.fontFamily, color: ctx.theme.text },
    ...ctx.animation
  }
}

export function legendOption (ctx: ChartContext, position: 'top' | 'bottom' = 'top'): Record<string, unknown> {
  return {
    type: 'scroll',
    [position]: 0,
    left: 'center',
    icon: 'roundRect',
    itemWidth: 10,
    itemHeight: 10,
    itemGap: 16,
    textStyle: { color: ctx.theme.textMuted, fontSize: ctx.theme.fontSize },
    pageTextStyle: { color: ctx.theme.textMuted }
  }
}

export function tooltipOption (ctx: ChartContext, trigger: 'axis' | 'item', axisPointer?: 'line' | 'shadow'): Record<string, unknown> {
  return {
    trigger,
    confine: true,
    backgroundColor: ctx.theme.surface,
    borderColor: ctx.theme.splitLine,
    borderWidth: 1,
    padding: [8, 12],
    textStyle: { color: ctx.theme.text, fontSize: ctx.theme.fontSize + 1 },
    extraCssText: 'border-radius: 8px; box-shadow: 0 8px 24px -12px rgba(16,24,40,.25);',
    ...(axisPointer ? { axisPointer: { type: axisPointer, shadowStyle: { color: withAlpha(ctx.theme.palette[0]!, 0.06) }, lineStyle: { color: ctx.theme.axisLine } } } : {})
  }
}

function axisStyle (ctx: ChartContext) {
  return {
    axisLine: { lineStyle: { color: ctx.theme.axisLine } },
    axisTick: { show: false },
    axisLabel: { color: ctx.theme.textMuted, fontSize: ctx.theme.fontSize, hideOverlap: true },
    splitLine: { lineStyle: { color: ctx.theme.splitLine, type: 'dashed' } },
    nameTextStyle: { color: ctx.theme.textMuted, fontSize: ctx.theme.fontSize }
  }
}

export function valueAxis (ctx: ChartContext, name?: string, position?: 'left' | 'right'): Record<string, unknown> {
  const style = axisStyle(ctx)
  return {
    type: 'value',
    ...(name ? { name, nameGap: 12 } : {}),
    ...(position ? { position } : {}),
    axisLine: { show: false },
    axisTick: style.axisTick,
    axisLabel: style.axisLabel,
    splitLine: position === 'right' ? { show: false } : style.splitLine,
    nameTextStyle: style.nameTextStyle
  }
}

export function categoryAxis (ctx: ChartContext, data: string[], name?: string): Record<string, unknown> {
  const style = axisStyle(ctx)
  return {
    type: 'category',
    data,
    ...(name ? { name, nameLocation: 'middle', nameGap: 28 } : {}),
    axisLine: style.axisLine,
    axisTick: style.axisTick,
    axisLabel: style.axisLabel,
    nameTextStyle: style.nameTextStyle
  }
}

export function fmt (spec: { format?: ValueFormat, currency?: string }, ctx: ChartContext, value: unknown): string | number | null {
  if (typeof value !== 'number') return null
  return spec.format ? formatValue(value, { format: spec.format, currency: spec.currency, locale: ctx.locale }) : value
}

export function valueFormatOf (spec: { format?: ValueFormat, currency?: string }) {
  return { format: spec.format ?? 'number', ...(spec.currency ? { currency: spec.currency } : {}) }
}

export function describeExtremes (categories: string[], values: (number | null)[], ctx: ChartContext, spec: { format?: ValueFormat, currency?: string }): string {
  let hi = -Infinity; let lo = Infinity; let hiAt = ''; let loAt = ''
  values.forEach((v, i) => {
    if (typeof v !== 'number') return
    if (v > hi) { hi = v; hiAt = categories[i] ?? '' }
    if (v < lo) { lo = v; loAt = categories[i] ?? '' }
  })
  if (!Number.isFinite(hi)) return ''
  return ` Highest ${fmt(spec, ctx, hi)} (${hiAt}), lowest ${fmt(spec, ctx, lo)} (${loAt}).`
}

// ---------------------------------------------------------------------------
// Cartesian: bar, line, area (and mixed via series.render)

function compileCartesian (spec: any, ctx: ChartContext): CompiledChart {
  const kind: 'bar' | 'line' | 'area' = spec.kind
  const categories: string[] = spec.categories
  const series: any[] = spec.series
  const hasRight = series.some(x => x.axis === 'right')
  const points = categories.length * series.length
  const showLegend = spec.showLegend ?? series.length > 1
  const horizontal = kind === 'bar' && !!spec.horizontal && !hasRight

  const compiledSeries = series.map((x, i) => {
    const render: 'bar' | 'line' | 'area' = x.render ?? kind
    const color = colorFor(ctx, i, x.intent)
    const base: Record<string, unknown> = {
      name: x.name,
      data: x.data,
      emphasis: { focus: 'series' },
      itemStyle: { color },
      ...(hasRight ? { yAxisIndex: x.axis === 'right' ? 1 : 0 } : {}),
      ...(spec.stacked || x.stack ? { stack: x.stack ?? 'total' } : {})
    }
    if (render === 'bar') {
      const radius = horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]
      return { ...base, type: 'bar', barMaxWidth: 36, itemStyle: { color, borderRadius: spec.stacked ? 0 : radius } }
    }
    return {
      ...base,
      type: 'line',
      smooth: spec.smooth ?? render === 'area',
      showSymbol: categories.length <= 24,
      symbol: 'circle',
      symbolSize: 6,
      lineStyle: { width: 2.5, color },
      ...(render === 'area'
        ? { areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: withAlpha(color, 0.28) }, { offset: 1, color: withAlpha(color, 0.02) }] } } }
        : {})
    }
  })

  if (spec.referenceLines?.length && compiledSeries[0]) {
    ;(compiledSeries[0] as Record<string, unknown>).markLine = {
      symbol: 'none',
      silent: true,
      label: { color: ctx.theme.textMuted, fontSize: ctx.theme.fontSize, formatter: '{b}' },
      data: spec.referenceLines.map((r: any) => ({
        name: r.label ?? '',
        [horizontal ? 'xAxis' : 'yAxis']: r.value,
        lineStyle: { color: colorFor(ctx, 0, r.intent ?? 'neutral'), type: 'dashed', width: 1.5 }
      }))
    }
  }

  const cat = categoryAxis(ctx, categories, spec.xLabel)
  const val = hasRight
    ? [valueAxis(ctx, spec.yLabel, 'left'), valueAxis(ctx, spec.rightLabel, 'right')]
    : valueAxis(ctx, spec.yLabel)

  const option: Record<string, unknown> = {
    ...baseOption(ctx),
    color: series.map((x, i) => colorFor(ctx, i, x.intent)),
    // containLabel ignores the last value label, which is centred on the right edge of horizontal bars.
    grid: { left: 8, right: horizontal ? 32 : hasRight ? 8 : 16, top: showLegend ? 40 : 16, bottom: spec.zoom ? 44 : 8, containLabel: true },
    tooltip: tooltipOption(ctx, 'axis', kind === 'bar' ? 'shadow' : 'line'),
    xAxis: horizontal ? val : { ...cat, boundaryGap: kind === 'bar' || series.some(x => x.render === 'bar') },
    yAxis: horizontal ? { ...cat, inverse: true } : val,
    series: compiledSeries,
    ...(showLegend ? { legend: legendOption(ctx) } : {}),
    ...(spec.zoom ? { dataZoom: [{ type: 'inside' }, { type: 'slider', height: 18, bottom: 8, borderColor: 'transparent', fillerColor: withAlpha(ctx.theme.palette[0]!, 0.12) }] } : {})
  }

  const modules = new Set<EChartsModule>(['GridComponent', 'TooltipComponent', 'AriaComponent'])
  compiledSeries.forEach(x => modules.add(x.type === 'bar' ? 'BarChart' : 'LineChart'))
  if (showLegend) modules.add('LegendComponent')
  if (spec.referenceLines?.length) modules.add('MarkLineComponent')
  if (spec.zoom) modules.add('DataZoomComponent')

  const table: TableFallback = {
    columns: [
      { key: 'category', label: spec.xLabel ?? ctx.labels.category },
      ...series.map((x, i) => ({ key: 's' + i, label: x.name, format: spec.format, align: 'end' as const }))
    ],
    rows: categories.map((c, ci) => {
      const row: Record<string, string | number | null> = { category: c }
      series.forEach((x, i) => { row['s' + i] = x.data[ci] ?? null })
      return row
    })
  }

  const noun = kind === 'bar' ? 'Bar chart' : kind === 'area' ? 'Area chart' : 'Line chart'
  const summary = `${spec.title ? spec.title + '. ' : ''}${noun} of ${series.map(x => x.name).join(', ')} across ${categories.length} ${spec.xLabel ?? ctx.labels.category.toLowerCase()} values.` +
    (series.length === 1 ? describeExtremes(categories, series[0].data, ctx, spec) : '')

  return { option, modules: [...modules], renderer: points > 3000 ? 'canvas' : 'svg', table, summary, valueFormat: valueFormatOf(spec) }
}

// ---------------------------------------------------------------------------
// Donut

const donutSchema = s.object({
  kind: s.literal('donut'),
  items: s.array(s.object({ label: LABEL, value: s.number({ min: 0 }), intent }, { required: ['label', 'value'] }), { min: 1, max: 24 }),
  centerLabel: s.string({ max: 40 }),
  centerValue: s.string({ max: 24 }),
  ...commonChartProps
}, { required: ['kind', 'items'] })

export function compileDonutLike (items: { label: string, value: number, color: string }[], spec: any, ctx: ChartContext): CompiledChart {
  const total = items.reduce((sum, i) => sum + i.value, 0)
  const showLegend = spec.showLegend ?? true
  const option: Record<string, unknown> = {
    ...baseOption(ctx),
    color: items.map(i => i.color),
    tooltip: tooltipOption(ctx, 'item'),
    ...(showLegend ? { legend: legendOption(ctx, 'bottom') } : {}),
    ...(spec.centerValue || spec.centerLabel
      ? {
          title: {
            text: spec.centerValue ?? '',
            subtext: spec.centerLabel ?? '',
            left: 'center',
            top: showLegend ? '38%' : '43%',
            itemGap: 4,
            textStyle: { color: ctx.theme.text, fontSize: 24, fontWeight: 700, fontFamily: ctx.theme.fontFamily },
            subtextStyle: { color: ctx.theme.textMuted, fontSize: ctx.theme.fontSize, fontFamily: ctx.theme.fontFamily }
          }
        }
      : {}),
    series: [{
      type: 'pie',
      radius: ['58%', '80%'],
      center: ['50%', showLegend ? '45%' : '50%'],
      avoidLabelOverlap: true,
      padAngle: 1.5,
      itemStyle: { borderRadius: 6, borderColor: ctx.theme.surface, borderWidth: 2 },
      label: { show: false },
      labelLine: { show: false },
      emphasis: { scale: true, scaleSize: 6 },
      data: items.map(i => ({ name: i.label, value: i.value, itemStyle: { color: i.color } }))
    }]
  }
  const modules: EChartsModule[] = ['PieChart', 'TooltipComponent', 'AriaComponent']
  if (showLegend) modules.push('LegendComponent')
  if (spec.centerValue || spec.centerLabel) modules.push('TitleComponent')
  const table: TableFallback = {
    columns: [
      { key: 'label', label: ctx.labels.category },
      { key: 'value', label: ctx.labels.value, format: spec.format, align: 'end' },
      { key: 'share', label: ctx.labels.share, format: 'percent', align: 'end' }
    ],
    rows: items.map(i => ({ label: i.label, value: i.value, share: total ? i.value / total : null }))
  }
  const top = [...items].sort((a, b) => b.value - a.value)[0]
  const summary = `${spec.title ? spec.title + '. ' : ''}Donut chart with ${items.length} parts totalling ${fmt(spec, ctx, total)}.` +
    (top && total ? ` Largest: ${top.label} (${Math.round(top.value / total * 100)} %).` : '')
  return { option, modules, renderer: 'svg', table, summary, valueFormat: valueFormatOf(spec) }
}

function compileDonut (spec: any, ctx: ChartContext): CompiledChart {
  const items = spec.items.map((i: any, idx: number) => ({ label: i.label, value: i.value, color: colorFor(ctx, idx, i.intent) }))
  return compileDonutLike(items, spec, ctx)
}

// ---------------------------------------------------------------------------
// Severity breakdown

const severityCounts: Record<string, s.Schema> = {}
for (const sev of SEVERITIES) severityCounts[sev] = s.number({ min: 0, integer: true })

const severitySchema = s.object({
  kind: s.literal('severity'),
  counts: s.object(severityCounts),
  variant: s.enumOf(['bar', 'donut']),
  ...commonChartProps
}, { required: ['kind', 'counts'] })

function compileSeverity (spec: any, ctx: ChartContext): CompiledChart {
  const present = SEVERITIES.filter(sev => typeof spec.counts[sev] === 'number')
  const items = present.map(sev => ({ sev, label: ctx.labels.severity[sev], value: spec.counts[sev] as number, color: ctx.theme.severities[sev] }))
  if (spec.variant === 'donut') {
    const total = items.reduce((sum, i) => sum + i.value, 0)
    return compileDonutLike(items, { ...spec, centerValue: String(total), format: 'integer' }, ctx)
  }
  const option: Record<string, unknown> = {
    ...baseOption(ctx),
    grid: { left: 8, right: 40, top: 8, bottom: 8, containLabel: true },
    tooltip: tooltipOption(ctx, 'axis', 'shadow'),
    xAxis: { ...valueAxis(ctx), minInterval: 1, splitLine: { show: false }, axisLabel: { show: false } },
    yAxis: { ...categoryAxis(ctx, items.map(i => i.label)), inverse: true, axisLine: { show: false } },
    series: [{
      type: 'bar',
      barMaxWidth: 22,
      showBackground: true,
      backgroundStyle: { color: withAlpha(ctx.theme.axisLine, 0.18), borderRadius: 999 },
      itemStyle: { borderRadius: 999 },
      label: { show: true, position: 'right', color: ctx.theme.text, fontWeight: 600, fontSize: ctx.theme.fontSize + 1 },
      data: items.map(i => ({ value: i.value, name: i.label, itemStyle: { color: i.color } }))
    }]
  }
  const total = items.reduce((sum, i) => sum + i.value, 0)
  return {
    option,
    modules: ['BarChart', 'GridComponent', 'TooltipComponent', 'AriaComponent'],
    renderer: 'svg',
    table: {
      columns: [{ key: 'label', label: ctx.labels.category }, { key: 'value', label: ctx.labels.value, format: 'integer', align: 'end' }],
      rows: items.map(i => ({ label: i.label, value: i.value }))
    },
    summary: `${spec.title ? spec.title + '. ' : ''}${total} findings: ` + items.map(i => `${i.value} ${i.label.toLowerCase()}`).join(', ') + '.',
    valueFormat: { format: 'integer' }
  }
}

// ---------------------------------------------------------------------------
// Registry

export const CHART_KINDS: Record<string, ChartKindDef> = {
  bar: { schema: cartesianSchema('bar'), compile: compileCartesian },
  line: { schema: cartesianSchema('line'), compile: compileCartesian },
  area: { schema: cartesianSchema('area'), compile: compileCartesian },
  donut: { schema: donutSchema, compile: compileDonut },
  severity: { schema: severitySchema, compile: compileSeverity }
}

export function registerChartKind (kind: string, def: ChartKindDef): void {
  CHART_KINDS[kind] = def
}

export function chartSchema (): s.Schema {
  const variants: Record<string, s.Schema> = {}
  for (const [kind, def] of Object.entries(CHART_KINDS)) variants[kind] = def.schema
  return s.oneOfBy('kind', variants, 'Chart spec; kind selects the chart type and its data shape')
}

export function chartContext (options: CompileOptions = {}): ChartContext {
  const labels: ChartLabels = {
    ...DEFAULT_CHART_LABELS,
    ...options.labels,
    severity: { ...DEFAULT_CHART_LABELS.severity, ...(options.labels?.severity ?? {}) }
  }
  return {
    theme: chartTheme(options.preset),
    locale: options.locale ?? 'en_US',
    labels,
    animation: options.reducedMotion
      ? { animation: false }
      : { animationDuration: 700, animationDurationUpdate: 400, animationEasing: 'cubicOut' }
  }
}

/** Compile a spec that has already passed validation. Throws on an unknown kind. */
export function compileChart (spec: { kind: string } & Record<string, unknown>, options: CompileOptions = {}): CompiledChart {
  const def = CHART_KINDS[spec.kind]
  if (!def) throw new Error(`Unknown chart kind ${spec.kind}`)
  return def.compile(spec, chartContext(options))
}
